// src/shared/utils/uiMode.js

export function readUiOverride() {
  try {
    const p = new URLSearchParams(window.location.search);
    const q = (p.get('ui') || '').toLowerCase();
    if (q === 'mobile' || q === 'desktop') return q;

    const saved = (localStorage.getItem('ui_override') || '').toLowerCase();
    if (saved === 'mobile' || saved === 'desktop') return saved;
  } catch (_) {}
  return null;
}

export function readViewportSize() {
  try {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const w = Math.max(1, Math.round(((vv?.width ?? window.innerWidth ?? 0) || 0) + 0.01));
    const h = Math.max(1, Math.round(((vv?.height ?? window.innerHeight ?? 0) || 0) + 0.01));
    return { w, h };
  } catch (_) {
    return { w: 1200, h: 700 };
  }
}

export function isRealMobileDevice() {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

    const nav = navigator;
    const ua = String(nav.userAgent || '');
    const platform = String(nav.platform || '');
    const maxTouchPoints = Number(nav.maxTouchPoints || 0);
    const { w, h } = readViewportSize();
    const minSide = Math.min(w, h);
    const maxSide = Math.max(w, h);

    if (nav.userAgentData?.mobile === true) return true;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;

    // iPadOS 13+ may expose a desktop-like Macintosh UA. Do not require
    // pointer: coarse because iPad + trackpad/desktop-site mode can report a
    // fine pointer while still needing mobile runtime behavior.
    const iPadDesktopUA = /Macintosh/i.test(ua) && maxTouchPoints > 1;
    if (iPadDesktopUA) return true;

    // Some Android WebView / in-app browsers can omit common mobile tokens.
    // Treat small touch devices as mobile, but avoid classifying normal desktop
    // narrow windows as mobile when there is no touch capability.
    const touchCapable = maxTouchPoints > 0 || !!window.matchMedia?.('(pointer: coarse)')?.matches;
    const mobileSizedTouchDevice = touchCapable && minSide <= 820 && maxSide <= 1400;
    if (mobileSizedTouchDevice && !/Win32|Win64|Windows NT/i.test(platform + ' ' + ua)) return true;

    return false;
  } catch (_) {
    return false;
  }
}

export function shouldUseMobileRuntimeFeatures() {
  try {
    const ov = readUiOverride();
    // Explicit mobile override is used for QA/testing and should behave like
    // the real mobile runtime. Automatic compact desktop mode still remains
    // desktop runtime because it has no override and isRealMobileDevice() is false.
    if (ov === 'mobile') return true;
    if (ov === 'desktop') return false;
    return isRealMobileDevice();
  } catch (_) {
    return false;
  }
}

function isLikelyMobileUserAgent() {
  return isRealMobileDevice();
}

export function shouldForceCompactViewportMobile(options = {}) {
  const allowCompactViewportMobile = options?.allowCompactViewportMobile !== false;
  if (!allowCompactViewportMobile) return false;

  const { w: vw } = readViewportSize();

  // [FIX][2026-08-16][ANCHOR:UI_MODE:COMPACT_VIEWPORT_FORCE_MOBILE_V6]
  // 데스크톱에서 DevTools를 아래에 열거나 브라우저 높이만 줄였다는 이유로
  // 모바일 Root로 전환되면 로비가 통째로 재마운트되어 모바일 로딩 화면이 튀어나온다.
  // 자동 compact 전환은 '폭'만 기준으로 하고, 실제 모바일 판정은 UA/touch 로직에 맡긴다.
  return vw <= 960;
}


export function detectUiAuto(options = {}) {
  // 기본 모바일 감지:
  // - 실제 모바일 UA는 mobile
  // - 작은 터치 화면은 mobile
  // - 로비/일반 화면은 창이 너무 좁거나 낮으면 mobile UI로 전환
  const { w: vw } = readViewportSize();
  const isSmall = window.matchMedia?.('(max-width: 768px)')?.matches ?? (vw <= 768);
  const hasTouch =
    navigator.maxTouchPoints > 0 ||
    window.matchMedia?.('(pointer: coarse)')?.matches;

  const uaMobile = isLikelyMobileUserAgent();
  const compactViewport = shouldForceCompactViewportMobile(options);

  return (uaMobile || compactViewport || (isSmall && hasTouch)) ? 'mobile' : 'desktop';
}

export function getUiMode(options = {}) {
  const ov = readUiOverride();
  if (ov === 'mobile' || ov === 'desktop') return ov;
  return detectUiAuto(options);
}
